"use client"

import { Loader2, Search } from "lucide-react"

import { DateRangeChip } from "@/components/graphite/date-range-chip"
import { SegmentedControl } from "@/components/graphite/segmented-control"
import { SelectChip } from "@/components/graphite/select-chip"
import { ShareBar, ShareLegend, type Share } from "@/components/graphite/share-bar"
import { TextAction } from "@/components/graphite/text-action"
import { avatarHex, stageTint, TINT } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { FeeBandKey, PeriodKey, PlacementRow } from "@/hooks/use-placements"

/**
 * The placements page, from canvas artboard 7c.
 *
 * 7c leads with the money as a picture: a "Revenue by company" card holding the
 * total, a stacked bar of every company's share, and a legend carrying the
 * figures. Under it the period presets and the filter chips, then one table.
 *
 * Turn 16 later redrew this page as hairline stat strips and two ranked lists,
 * arguing against the charts. 7c is the one being built — the user's own source
 * of truth for this page.
 *
 * Two things 7c draws are not here, because nothing can feed them: the status
 * pill on each row (a placement carries no status) and the PLC-#### reference
 * under each name (the id is a UUID). Both are backend asks.
 *
 * Purely presentational — every figure, row and handler arrives from
 * `usePlacements`.
 */

/**
 * 7c's columns, at the widths it draws them — with the avatar broken out into a
 * column of its own so a narrow row can hang two lines off it.
 */
const WIDE =
  "@2xl:grid-cols-[auto_minmax(0,1.5fr)_minmax(0,1.6fr)_110px_120px] @2xl:items-center @2xl:gap-x-4 @2xl:px-[18px] @3xl:grid-cols-[auto_minmax(0,1.5fr)_minmax(0,1.6fr)_110px_120px_minmax(0,130px)]"

/**
 * A row, which is two lines until there is room for one.
 *
 * Three columns do not fit in a phone-width pane. At 390 the candidate column
 * resolved to about 96px, and after the avatar and its gap the name had ~50px
 * to live in — "Maya …", beside a fee column with room to spare. So below @2xl
 * the row stacks: who and how much on the first line, what for on the second.
 */
const ROW = cn(
  "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 px-4 py-3",
  WIDE,
  "@2xl:py-0",
)

/** A boxed control's shell, which 7c uses for the search and the company pick. */
const BOXED =
  "flex items-center gap-2.5 rounded-md border bg-card px-3 py-2.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary"

function Row({ row, onOpen }: { row: PlacementRow; onOpen: () => void }) {
  const hex = avatarHex(row.id)

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        ROW,
        "min-h-[62px] w-full border-t text-left transition-colors hover:bg-foreground/[0.03] @2xl:h-[62px]",
      )}
    >
      {/* Both lines hang off the avatar while the row is stacked. */}
      <span
        aria-hidden
        className="row-span-2 flex size-8 flex-none items-center justify-center rounded-full text-[11px] font-medium @2xl:row-span-1"
        style={{ color: hex, backgroundColor: stageTint(hex, TINT.pill) }}
      >
        {row.initials}
      </span>

      <span className="min-w-0">
        <span className="block truncate text-[13px] font-medium leading-[1.2]">
          {row.name}
        </span>
        <span className="mt-[5px] block truncate font-mono text-[10.5px] leading-none text-muted-foreground">
          {row.type}
        </span>
      </span>

      {/*
        Money is the subject of this page, so it carries the brand. Stacked it
        sits at the end of the name's line; in one row it takes 7c's third
        column, which is why the order is set rather than left to the markup.
      */}
      <span className="whitespace-nowrap font-mono text-[13px] font-medium text-brand-ink @2xl:order-2">
        {row.fee}
      </span>

      <span className="col-span-2 min-w-0 truncate text-[12.5px] font-medium leading-[1.3] @2xl:order-1 @2xl:col-span-1">
        {row.jobTitle}
        <span className="font-normal text-muted-foreground"> @{row.company}</span>
      </span>

      <span className="hidden font-mono text-[12px] text-muted-foreground @2xl:order-3 @2xl:block">
        {row.from ?? "—"}
      </span>

      {/* @3xl, not @4xl: the ATS panel measures ~780 at a 1440 window, and a
          column gated above that is a column nobody ever sees. */}
      <span className="hidden min-w-0 truncate font-mono text-[11px] text-muted-foreground @2xl:order-4 @3xl:block">
        {row.splits.join(" · ")}
      </span>
    </button>
  )
}

export function GraphitePlacements({
  shares,
  revenue,
  companyCount,
  periodLabel,
  rows,
  inPeriodCount,
  rangeRevenue,
  companies,
  company,
  onCompanyChange,
  search,
  onSearchChange,
  period,
  periodOptions,
  onPeriodChange,
  fee,
  feeOptions,
  onFeeChange,
  from,
  to,
  onFromChange,
  onToChange,
  onClearPeriod,
  hasPeriod,
  onOpen,
  isLoading,
  isError,
}: {
  shares: Share[]
  revenue: string
  companyCount: number
  periodLabel: string
  rows: PlacementRow[]
  inPeriodCount: number
  rangeRevenue: string
  companies: string[]
  company: string
  onCompanyChange: (next: string) => void
  search: string
  onSearchChange: (next: string) => void
  period: PeriodKey
  periodOptions: { value: PeriodKey; label: string }[]
  onPeriodChange: (next: PeriodKey) => void
  fee: FeeBandKey
  feeOptions: { value: FeeBandKey; label: string }[]
  onFeeChange: (next: FeeBandKey) => void
  from: string
  to: string
  onFromChange: (next: string) => void
  onToChange: (next: string) => void
  onClearPeriod: () => void
  hasPeriod: boolean
  onOpen: (row: PlacementRow) => void
  isLoading: boolean
  isError: boolean
}) {
  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* 7c's first row: what to look for, and where. */}
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-4 @2xl:px-[26px]">
          <span className={cn(BOXED, "min-w-0 flex-1")}>
            <Search className="size-3.5 flex-none text-muted-foreground" />
            <input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search placements (candidate, company, job, split owner)"
              aria-label="Search placements"
              className="min-w-0 flex-1 bg-transparent text-[12.5px] leading-none outline-none placeholder:text-muted-foreground"
            />
          </span>

          {/*
            A real list, not a transparent `select` over a picture of one. It
            keeps the search row's taller box so the two line up, and its value
            is a name rather than a figure, so it is not set in mono.
          */}
          <SelectChip
            options={[
              { value: "all", label: "All companies" },
              ...companies.map((name) => ({ value: name, label: name })),
            ]}
            value={company}
            onChange={onCompanyChange}
            listLabel="Filter by company"
            active={company !== "all"}
            mono={false}
            className="flex-none"
            triggerClassName="max-w-[220px] gap-2.5 py-2.5"
          />

          {hasPeriod && (
            <TextAction onClick={onClearPeriod} className="flex-none px-1 text-[12px]">
              Clear
            </TextAction>
          )}
        </div>

        {/*
          The money as a picture. 7c: the total, then one bar carrying every
          company's share of it, then the figures underneath.
        */}
        <div className="px-4 pb-3.5 @2xl:px-[26px]">
          <div className="rounded-[10px] border bg-card p-4 @2xl:px-[18px]">
            <div className="mb-3.5 flex items-baseline gap-3">
              <span className="text-[11.5px] font-medium text-muted-foreground">
                Revenue by company
              </span>
              <span className="ml-auto whitespace-nowrap text-[11px] text-muted-foreground">
                {periodLabel}
              </span>
            </div>

            {isLoading || isError ? (
              /*
                "No revenue in this period" is a financial claim, and `shares`
                is an empty array until the request lands — so the card stated
                it before anything had been read, and permanently after a
                failure. The table said so further down; the money did not.
              */
              <p className="py-2 text-[12.5px] text-muted-foreground">
                {isError
                  ? "Placements cannot be read right now."
                  : "Reading placements…"}
              </p>
            ) : shares.length === 0 ? (
              <p className="py-2 text-[12.5px] text-muted-foreground">
                No revenue in this period.
              </p>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                  <span className="font-mono text-[26px] leading-none text-brand-ink">
                    {revenue}
                  </span>
                  <span className="text-[11.5px] text-muted-foreground">
                    landed across {companyCount}{" "}
                    {companyCount === 1 ? "company" : "companies"}
                  </span>
                </div>

                <ShareBar shares={shares} />
                <ShareLegend shares={shares} className="mt-2.5" />
              </>
            )}
          </div>
        </div>

        {/* The period, and the two chips that narrow it further. */}
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3.5 @2xl:px-[26px]">
          <SegmentedControl
            label="Period"
            options={periodOptions}
            value={period}
            onChange={onPeriodChange}
          />

          {/*
            One range, not two dates — canvas 31a. The two chips that stood here
            each hid a transparent native date input, which is what made them
            hard to hit and impossible to style, and neither knew the other was
            the far end of the same window.
          */}
          <DateRangeChip
            from={from}
            to={to}
            onChange={(next) => {
              onFromChange(next.from)
              onToChange(next.to)
            }}
          />

          <SelectChip
            label="Fee"
            options={feeOptions}
            value={fee}
            onChange={onFeeChange}
            listLabel="Filter by fee"
            active={fee !== "any"}
          />

          <span className="ml-auto whitespace-nowrap text-[11.5px] text-muted-foreground">
            {isLoading || isError
              ? "—"
              : `${rows.length} of ${inPeriodCount} placements · ${rangeRevenue} in range`}
          </span>
        </div>

        <div className="px-4 pb-6 @2xl:px-[26px]">
          <div className="overflow-hidden rounded-[10px] border bg-card">
            {/*
              Column labels belong to columns. Stacked rows have none, so the
              header is only there once the row is one line.
            */}
            <div
              className={cn(
                "hidden @2xl:grid",
                WIDE,
                "h-11 text-[11px] font-medium tracking-[0.04em] text-muted-foreground",
              )}
            >
              <div className="col-span-2">Candidate</div>
              <div>Job</div>
              <div>Fee</div>
              <div>Start</div>
              <div className="hidden @3xl:block">Splits</div>
            </div>

            {isLoading && (
              <p className="flex items-center gap-2 border-t p-4 text-[12.5px] text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Loading placements…
              </p>
            )}

            {isError && (
              <p className="border-t p-4 text-[12.5px] text-muted-foreground">
                Placements could not be loaded.
              </p>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <p className="border-t p-6 text-[12.5px] text-muted-foreground">
                No placements match these filters.
              </p>
            )}

            {rows.map((row) => (
              <Row key={row.id} row={row} onOpen={() => onOpen(row)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
